import { getElements } from '../Utility/Dom'
import { reinitOnSwap } from '../Utility/SwapReinit'

// Toggle handler for the dummy filter dropdowns + Mehr-Filter sidebar.
// Desktop: tapping a chip shows that panel only and hides the rest.
// Mobile (<tablet): tapping a chip opens the unified flyout (all panel headers
// visible), expands the clicked section's body, collapses the others.
// Accordion headers inside the flyout (data-js=search-filter-accordion) toggle
// their own panel's data-expanded attribute.
class CourseFilterToggle {
  private readonly accordions: NodeListOf<HTMLElement>
  private readonly backers: NodeListOf<HTMLElement>
  private readonly clears: NodeListOf<HTMLElement>
  private readonly closers: NodeListOf<HTMLElement>
  private readonly drills: NodeListOf<HTMLElement>
  private readonly triggers: NodeListOf<HTMLElement>

  constructor() {
    this.triggers = getElements('search-filter-trigger')
    this.closers = getElements('search-filter-close')
    this.accordions = getElements('search-filter-accordion')
    this.clears = getElements('search-filter-clear')
    this.drills = getElements('search-filter-topic-drill')
    this.backers = getElements('search-filter-topic-back')
  }

  public init(): void {
    this.triggers.forEach((trigger): void => {
      if (!(trigger instanceof HTMLButtonElement)) return

      trigger.addEventListener('click', (event): void => {
        const targetId = trigger.getAttribute('data-target')
        if (!targetId) return

        const target = document.getElementById(targetId)
        if (!target) return

        const willOpen = trigger.getAttribute('aria-expanded') !== 'true'
        this.closeAll()

        if (willOpen) {
          trigger.setAttribute('aria-expanded', 'true')
          target.hidden = false

          if (window.matchMedia('(max-width: 767px)').matches) {
            const panels = target.parentElement?.querySelectorAll<HTMLElement>('.search-filter__panel') ?? []
            panels.forEach((panel): void => {
              panel.hidden = false
              if (panel === target) {
                panel.setAttribute('data-expanded', '')
                this.syncAccordionExpanded(panel, true)
              } else {
                panel.removeAttribute('data-expanded')
                this.syncAccordionExpanded(panel, false)
              }
            })
          } else {
            target.setAttribute('data-expanded', '')
            this.syncAccordionExpanded(target, true)
          }

          // Keyboard activation (Enter/Space) sets MouseEvent.detail to 0 —
          // move focus into the panel so the user can interact without
          // tabbing past every other chip. Mouse clicks (detail >= 1) leave
          // focus on the trigger to avoid an unexpected scroll jump.
          if (event.detail === 0) {
            this.focusFirstInteractive(target)
          }
        }
      })
    })

    this.accordions.forEach((accordion): void => {
      if (!(accordion instanceof HTMLButtonElement)) return

      accordion.addEventListener('click', (): void => {
        const panel = accordion.closest<HTMLElement>('.search-filter__panel')
        if (!panel) return

        const willOpen = !panel.hasAttribute('data-expanded')

        // Exclusive accordion — opening one section collapses any others.
        panel.parentElement?.querySelectorAll<HTMLElement>('.search-filter__panel[data-expanded]').forEach((other): void => {
          if (other !== panel) {
            other.removeAttribute('data-expanded')
            this.syncAccordionExpanded(other, false)
          }
        })

        if (willOpen) {
          panel.setAttribute('data-expanded', '')
          this.syncAccordionExpanded(panel, true)
        } else {
          panel.removeAttribute('data-expanded')
          this.syncAccordionExpanded(panel, false)
        }
      })
    })

    this.closers.forEach((closer): void => {
      if (!(closer instanceof HTMLButtonElement)) return

      closer.addEventListener('click', (): void => {
        const focused = this.findOpenTrigger()
        this.closeAll()
        focused?.focus()
      })
    })

    this.clears.forEach((clear): void => {
      if (!(clear instanceof HTMLButtonElement)) return

      clear.addEventListener('click', (): void => {
        const input = clear.parentElement?.querySelector<HTMLInputElement>('input[type="search"]')
        if (!input) return
        input.value = ''
        input.form?.requestSubmit()
      })
    })

    this.drills.forEach((drill): void => {
      if (!(drill instanceof HTMLButtonElement)) return

      drill.addEventListener('click', (event): void => {
        const container = drill.closest<HTMLElement>('[data-js="search-filter-topics"]')
        if (!container) return

        // Mobile: just toggle inline children expansion on the parent <li>.
        // Desktop: open the corresponding aside children panel (drill-in).
        if (window.matchMedia('(max-width: 767px)').matches) {
          const li = drill.closest<HTMLElement>('li')
          if (!li) return
          const isOpen = li.classList.toggle('is-expanded')
          drill.setAttribute('aria-expanded', isOpen ? 'true' : 'false')
          if (isOpen && event.detail === 0) {
            li.querySelector<HTMLInputElement>('ul input[type="checkbox"]')?.focus()
          }
          return
        }

        const targetId = drill.getAttribute('data-target')
        if (!targetId) return

        container.querySelectorAll<HTMLElement>('.search-filter__topics-children').forEach((el): void => {
          el.hidden = el.id !== targetId
        })
        container.querySelectorAll<HTMLButtonElement>('[data-js="search-filter-topic-drill"]').forEach((btn): void => {
          btn.setAttribute('aria-expanded', btn === drill ? 'true' : 'false')
        })
        container.classList.add('is-split')

        // Keyboard activation lands focus on the first sub-topic checkbox so
        // the user can dive in without tabbing past the drill button and the
        // back button. Mouse click leaves focus on the cursor target.
        if (event.detail === 0) {
          document.getElementById(targetId)
            ?.querySelector<HTMLInputElement>('input[type="checkbox"]')
            ?.focus()
        }
      })
    })

    this.backers.forEach((back): void => {
      if (!(back instanceof HTMLButtonElement)) return

      back.addEventListener('click', (): void => {
        const container = back.closest<HTMLElement>('[data-js="search-filter-topics"]')
        if (!container) return

        // Capture the drill-trigger BEFORE we reset aria-expanded — focus
        // returns to the chevron the user originally activated.
        const triggerToRestore = container.querySelector<HTMLButtonElement>(
          '[data-js="search-filter-topic-drill"][aria-expanded="true"]'
        )

        container.querySelectorAll<HTMLElement>('.search-filter__topics-children').forEach((el): void => {
          el.hidden = true
        })
        container.querySelectorAll<HTMLButtonElement>('[data-js="search-filter-topic-drill"]').forEach((btn): void => {
          btn.setAttribute('aria-expanded', 'false')
        })
        container.classList.remove('is-split')

        triggerToRestore?.focus()
      })
    })

    // Sync `data-has-selected` on each drill button with the checked state of
    // its children panel. Lets the chevron show a persistent indicator even
    // when the user has navigated back to the over-topic list.
    document.querySelectorAll<HTMLElement>('.search-filter__topics-children').forEach((panel): void => {
      const drill = document.querySelector<HTMLButtonElement>(`[data-target="${panel.id}"]`)
      if (!drill) return

      const sync = (): void => {
        const hasSelected = panel.querySelector<HTMLInputElement>('input[type="checkbox"]:checked') !== null
        drill.toggleAttribute('data-has-selected', hasSelected)
      }

      panel.querySelectorAll<HTMLInputElement>('input[type="checkbox"]').forEach((cb): void => {
        cb.addEventListener('change', sync)
      })

      sync()
    })
  }

  private closeAll(): void {
    this.triggers.forEach((trigger): void => {
      trigger.setAttribute('aria-expanded', 'false')
      const targetId = trigger.getAttribute('data-target')
      if (!targetId) return
      const target = document.getElementById(targetId)
      if (target) {
        target.hidden = true
        target.removeAttribute('data-expanded')
        this.syncAccordionExpanded(target, false)
      }
    })
  }

  private findOpenTrigger(): HTMLButtonElement | null {
    return document.querySelector<HTMLButtonElement>(
      '[data-js="search-filter-trigger"][aria-expanded="true"]'
    )
  }

  // Excludes the accordion header (already focused on the trigger) and the
  // close button (Escape covers that path), so keyboard users land on the
  // first content interaction — search input for Was, first checkbox for
  // Wann/Wo/Mehr.
  private focusFirstInteractive(panel: HTMLElement): void {
    const selector = [
      'input:not([type="hidden"]):not([tabindex="-1"])',
      'select:not([tabindex="-1"])',
      'textarea:not([tabindex="-1"])',
      'button:not([data-js="search-filter-accordion"]):not([data-js="search-filter-close"]):not([tabindex="-1"])',
      'a[href]:not([tabindex="-1"])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(', ')
    const first = panel.querySelector<HTMLElement>(selector)
    first?.focus()
  }

  private syncAccordionExpanded(panel: HTMLElement, expanded: boolean): void {
    panel
      .querySelectorAll<HTMLButtonElement>('[data-js="search-filter-accordion"]')
      .forEach((btn): void => {
        btn.setAttribute('aria-expanded', expanded ? 'true' : 'false')
      })
  }
}

const courseFilterToggle = new CourseFilterToggle()
courseFilterToggle.init()
reinitOnSwap(() => new CourseFilterToggle())

// Close any open filter panel when the user clicks outside the filter section.
// Listener lives in module scope so it survives AJAX swaps without re-binding
// (queries the current DOM at click time). Mobile flyout has its own close
// button; on desktop the panels are dropdowns and need outside-click handling.
document.addEventListener('click', (event): void => {
  const filterSection = document.querySelector<HTMLElement>('[data-js="search-filter"]')
  if (filterSection && !filterSection.contains(event.target as Node)) {
    filterSection
      .querySelectorAll<HTMLElement>('[data-js="search-filter-trigger"][aria-expanded="true"]')
      .forEach((trigger): void => {
        trigger.setAttribute('aria-expanded', 'false')
        const targetId = trigger.getAttribute('data-target')
        if (!targetId) return
        const target = document.getElementById(targetId)
        if (target) {
          target.hidden = true
          target.removeAttribute('data-expanded')
          target
            .querySelectorAll<HTMLButtonElement>('[data-js="search-filter-accordion"]')
            .forEach((btn): void => btn.setAttribute('aria-expanded', 'false'))
        }
      })
  }

  // The sort dropdown lives in the result region, outside the filter section,
  // so it needs its own outside-click close (the block above only covers the
  // filter panels). Its open/close is otherwise driven by the shared trigger
  // toggle logic above.
  const sortDropdown = document.querySelector<HTMLElement>('[data-js="search-filter-sort-dropdown"]')
  if (sortDropdown && !sortDropdown.contains(event.target as Node)) {
    const trigger = sortDropdown.querySelector<HTMLElement>('[data-js="search-filter-trigger"][aria-expanded="true"]')
    if (trigger) {
      trigger.setAttribute('aria-expanded', 'false')
      const targetId = trigger.getAttribute('data-target')
      const target = targetId ? document.getElementById(targetId) : null
      if (target) {
        target.hidden = true
        target.removeAttribute('data-expanded')
      }
    }
  }
})

// Focus-trap inside the open filter panel. The panel renders as an overlay on
// desktop and the desktop footer (with Reset link) is `display: none`, so the
// last sub-checkbox would otherwise hand focus straight to the result list.
// Keep keyboard users within the panel until they actively close it (Escape,
// click outside, or the panel's own × button).
const PANEL_TABBABLE_SELECTOR = [
  'a[href]:not([tabindex="-1"])',
  'button:not([disabled]):not([tabindex="-1"])',
  'input:not([disabled]):not([type="hidden"]):not([tabindex="-1"])',
  'select:not([disabled]):not([tabindex="-1"])',
  'textarea:not([disabled]):not([tabindex="-1"])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ')

const collectVisibleTabbables = (root: HTMLElement): HTMLElement[] =>
  Array.from(root.querySelectorAll<HTMLElement>(PANEL_TABBABLE_SELECTOR)).filter((el) => {
    let node: HTMLElement | null = el
    while (node && node !== root) {
      const style = getComputedStyle(node)
      if (node.hidden || style.display === 'none' || style.visibility === 'hidden') return false
      node = node.parentElement
    }
    return true
  })

document.addEventListener('keydown', (event): void => {
  if (event.key !== 'Tab') return
  const filterSection = document.querySelector<HTMLElement>('[data-js="search-filter"]')
  if (!filterSection) return
  const openPanel = filterSection.querySelector<HTMLElement>('.search-filter__panel:not([hidden])')
  if (!openPanel) return
  if (!(document.activeElement instanceof HTMLElement)) return
  if (!openPanel.contains(document.activeElement)) return

  const tabbables = collectVisibleTabbables(openPanel)
  if (tabbables.length === 0) return

  const first = tabbables[0]
  const last = tabbables[tabbables.length - 1]

  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault()
    last.focus()
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault()
    first.focus()
  }
})

// Escape closes any open filter panel and returns focus to its trigger button.
document.addEventListener('keydown', (event): void => {
  if (event.key !== 'Escape') return

  // Document-wide so the sort trigger (in the result region) is covered too.
  const openTrigger = document.querySelector<HTMLButtonElement>(
    '[data-js="search-filter-trigger"][aria-expanded="true"]'
  )
  if (!openTrigger) return

  const targetId = openTrigger.getAttribute('data-target')
  if (!targetId) return
  const target = document.getElementById(targetId)
  if (!target) return

  event.preventDefault()
  openTrigger.setAttribute('aria-expanded', 'false')
  target.hidden = true
  target.removeAttribute('data-expanded')
  target
    .querySelectorAll<HTMLButtonElement>('[data-js="search-filter-accordion"]')
    .forEach((btn): void => btn.setAttribute('aria-expanded', 'false'))
  openTrigger.focus()
})

// Panel search clear: click × next to the topic-search input → clear + re-filter list.
document.addEventListener('click', (event): void => {
  const btn = (event.target as Element | null)?.closest<HTMLButtonElement>('[data-js="search-filter-panel-clear"]')
  if (!btn) return
  const input = btn.closest('[data-js="search-filter-panel-search"]')?.querySelector<HTMLInputElement>('input')
  if (!input) return
  input.value = ''
  input.dispatchEvent(new Event('input', {bubbles: true}))
  input.focus()
})

// Wann shortcuts: click "In N Monaten" → check next N calendar months, trigger live filter.
// Delegated on document so it survives AJAX swaps of the filter region.
document.addEventListener('click', (event): void => {
  const btn = (event.target as Element | null)?.closest<HTMLButtonElement>('[data-js="wann-shortcut"]')
  if (!btn) return
  const months = parseInt(btn.dataset.months ?? '0', 10)
  if (!months) return
  setWannCheckboxes(buildNextMonthValues(months))
})

const buildNextMonthValues = (count: number): Set<string> => {
  const now = new Date()
  return new Set(
    Array.from({length: count}, (_, i) => {
      const d = new Date(now.getFullYear(), now.getMonth() + 1 + i, 1)
      return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}`
    })
  )
}

const setWannCheckboxes = (targets: Set<string>): void => {
  const checkboxes = document.querySelectorAll<HTMLInputElement>(
    'input[name="tx_sitepackage_coursesearch[filter][wann][]"]'
  )
  let changed = false
  checkboxes.forEach((cb): void => {
    const shouldBeChecked = targets.has(cb.value)
    if (cb.checked !== shouldBeChecked) {
      cb.checked = shouldBeChecked
      changed = true
    }
  })
  if (changed && checkboxes.length > 0) {
    checkboxes[0].dispatchEvent(new Event('change', {bubbles: true}))
  }
}

// Enter on a filter checkbox/radio toggles it instead of submitting the form.
// Native checkboxes only respond to Space, but keyboard users intuitively press
// Enter — which would otherwise trigger the form's implicit submit (on the hero:
// navigation to the result page; on the result page: an AJAX refresh with the
// value unchanged). Routing Enter through `click()` flips the box and fires the
// `change` the live filter/count listeners rely on. Module scope so it covers
// both the result page and the hero and survives AJAX swaps.
document.addEventListener('keydown', (event): void => {
  if (event.key !== 'Enter') return
  const target = event.target
  if (!(target instanceof HTMLInputElement)) return
  if (target.type !== 'checkbox' && target.type !== 'radio') return
  if (!target.closest('[data-js="search-filter"]')) return
  event.preventDefault()
  target.click()
})
